import { SiteUrls } from '@tableau/shared-urls';
import {
  ButtonType, ButtonWidget,
  RadioButtonWithLabelGroupWidget, RadioButtonWithLabelOption, TextFieldWidget, Tooltip, TooltipPosition, TooltipProps
} from '@tableau/widgets';
import { FormikProps, withFormik } from 'formik';
import React from 'react';
import _ from 'underscore';

import { ButtonWithSpinner } from '~commonInfra/components/ButtonWithSpinner';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { UrlParamKeys } from '~commonInfra/url/UrlParamKeys';
import { Filter } from '~commonInfra/utils/Filter';
import { Messages } from '~src/codegen/Localize';
import { ActionButtonLeftWide, ActionButtons } from '~src/commonInfra/dialogs/ReactActionDialog';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { imgPath } from '~src/commonInfra/utils/ImgUrl';
import { MAX_PROJECT_NAME_LENGTH } from '~src/projects/components/CreateProjectDialogBody';
import { Projects } from '~src/projects/resources/Projects';
import {
  SalesforceIntegrationCancelButtonClickEvent,
  SalesforceIntegrationPublishButtonClickEvent
} from '~src/settings/IntegrationsSettings/telemetry/IntegrationsSettingsTelemetry';
import { FilterFieldNames } from '~src/spacesPlatform/models/IFilterClause';
import { OrderKeys } from '~src/spacesPlatform/models/OrderKeys';
import { Order } from '~src/spacesPlatform/models/sort/Order';
import { IOrderClause } from '~src/spacesPlatform/serverApi/types/IOrderClause';
import { IUserInfo } from '~src/userManagement/users/models/IUserInfo';

import { UploadBundleDialogStyles } from '../styles/UploadBundleDialogStyles';

export const ZERO_WIDTH_SPACE = '\u200B';
export const CLOUD_OPTIONS_GROUP_LABEL_ID = 'cloud-options-group-label';
export const PERMISSION_OPTIONS_GROUP_LABEL_ID = 'permission-options-group-label';

export namespace UploadBundlesDialogTestIDs {
  export const CLOUD_OPTION_PREFIX = 'upload-bundles-dialog-bundle-option-';
  export const PROJECT_NAME_INPUT = 'upload-bundles-dialog-project-name';
  export const PROJECT_EXPLORER = 'upload-bundles-dialog-project';
  export const CANCEL = 'upload-bundles-dialog-cancel';
  export const SUBMIT = 'upload-bundles-dialog-submit';
  export const CLOUD_OPTIONS = 'upload-bundles-cloud-options';
  export const PERMISSIONS = 'upload-bundles-project-permissions-options';
}

// Form Values
export interface UploadBundlesDialogValues {
  bundleIds: string[];
  projectName: string;
  projectDescription: string;
  contentPermissions: string;
}

export interface UploadBundlesDispatchProps {
  uploadBundlesActionDispatch: (values: UploadBundlesDialogValues, oauthId: number, myContentUrl: string) => void;
}

export interface UploadBundlesExternalProps {
  readonly oauthId: number;
  readonly close: () => void;
}

export interface UploadBundlesPropsFromReduxState {
  readonly siteUrls: SiteUrls;
  readonly user: IUserInfo;
}

export enum BundleContentPermissions {
  adminOnly = 'AdminOnly',
  inherited = 'InheritFromParent',
}

enum KnownBundles {
  salesCloud = 'salesCloud',
  serviceCloud = 'serviceCloud',
  nonprofitCloud = 'nonprofitCloud',
  salesforceAdminInsights = 'salesforceAdminInsights',
}

// type safe identification of the enum members
export type KnownBundlesKeys = keyof typeof KnownBundles;

const bundleToLabel = (bundle: KnownBundlesKeys): string => {
  switch (bundle) {
    case 'salesCloud': return 'Sales Cloud';
    case 'serviceCloud': return 'Service Cloud';
    case 'nonprofitCloud': return 'Nonprofit Cloud';
    case 'salesforceAdminInsights': return 'Salesforce Admin Insights';
    default:
      throw new Error('Unknown bundle type given.');
  }
};

export type UploadBundlesDialogProps = UploadBundlesDispatchProps & UploadBundlesPropsFromReduxState & UploadBundlesExternalProps;
export type InternalUploadBundlesDialogFormProps = UploadBundlesDialogProps & FormikProps<UploadBundlesDialogValues>;

export function InternalUploadBundlesDialogForm(props: InternalUploadBundlesDialogFormProps) {
  // Hardcoded bundle values
  const availableBundles: KnownBundlesKeys[] = [
    'nonprofitCloud',
    'salesCloud',
    'salesforceAdminInsights',
    'serviceCloud'
  ];

  const [isBusy, setIsBusy] = React.useState<boolean>(false);

  const handleSubmit = () => {
    SalesforceIntegrationPublishButtonClickEvent(props.values.bundleIds);
    setIsBusy(true);

    const { projectName } = props.values;
    getTopLevelProjectsWithName(projectName)
      .then(count => {
        if (count) {
          projectNameAlreadyExists();
        } else {
          projectNameDoesNotExist();
        }
      }).catch(() => {
        // Should only happen if something catastrophic occurs in the backend
        setIsBusy(false);
        ToasterService.instance.pop({
          type: ToastType.Error,
          text: Messages.errorUnknown(),
          keepOnLocationChange: true,
        });
      });
  };

  const handleCancel = () => {
    SalesforceIntegrationCancelButtonClickEvent(props.values.bundleIds);
    props.close();
  };

  const projectNameDoesNotExist = () => {
    const mostRecentContentFirstClause = Order.desc(OrderKeys.updatedAt);
    let myContentUrl = props.siteUrls.userContentUrl(props.user.username, props.user.domainName);
    myContentUrl += createOrderQuery(mostRecentContentFirstClause);

    props.uploadBundlesActionDispatch(props.values, props.oauthId, myContentUrl);
    props.close();
  };

  /**
   * Returns "?order=[query]"
  */
  function createOrderQuery(orderClause: IOrderClause): string {
    const query = Order.serialize([orderClause]);
    return `?${UrlParamKeys.Order}=${query}`;
  }

  const projectNameAlreadyExists = () => {
    setIsBusy(false);
    props.setFieldError('projectName', Messages.errorProjectNameAlreadyExists());
  };

  const handlePermissionChange = (value: string): void => {
    props.setFieldValue('contentPermissions', value);
  };

  const handleCloudOptionChange = (value: string): void => {
    const buttonId = [];
    buttonId.push(value);
    props.setFieldValue('bundleIds', buttonId);
  };

  return (
    <>
      <section className={UploadBundleDialogStyles.Section}>
        {getSectionHeader(Messages.connectToSalesforce_selectCloudHeader())}
        <div className={UploadBundleDialogStyles.SectionContent}>
          {getSectionDescription(Messages.connectToSalesforce_selectCloudDescription())}
          { getCloudOptions()}
        </div>
      </section>
      <section className={UploadBundleDialogStyles.Section}>
        {getSectionHeader(Messages.connectToSalesforce_createAProjectHeader())}
        <div className={UploadBundleDialogStyles.SectionContent}>
          {getProjectNameField()}
          {getProjectPermissionOptions()}
        </div>
      </section>
      {getActionButtons()}
    </>
  );

  function getSectionHeader(text: string) {
    return <h2 className={UploadBundleDialogStyles.SectionHeader}>{text}</h2>;
  }

  function getSectionDescription(text: string) {
    return <p className={UploadBundleDialogStyles.SectionDescription}>{text}</p>;
  }

  function getProjectNameField() {
    return <TextFieldWidget
      className={UploadBundleDialogStyles.TextField}
      kind={'line'}
      label={Messages.connectToSalesforce_newProjectName()}
      maxLength={MAX_PROJECT_NAME_LENGTH}
      message={props.errors.projectName ?? ZERO_WIDTH_SPACE}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        props.setFieldValue('projectName', e.target.value);
      }}
      onClear={() => props.setFieldValue('projectName', '')}
      style={UploadBundleDialogStyles.FullWidthStyle}
      testId={UploadBundlesDialogTestIDs.PROJECT_NAME_INPUT}
      value={props.values.projectName}
      valid={props.errors.projectName ? false : undefined}
    />;
  }

  function getProjectPermissionOptions() {
    return <ProjectPermissionOptions
      handleChange={handlePermissionChange}
    />;
  }

  function getCloudOptions() {
    const buttonList = availableBundles.map((bundle) => {
      return {
        label: bundleToLabel(bundle),
        testId: UploadBundlesDialogTestIDs.CLOUD_OPTION_PREFIX + bundle.toString(),
        value: bundle,
      };
    });
    return <CloudOptions
      handleChange={handleCloudOptionChange}
      buttonList={buttonList}
    />;
  }

  function getActionButtons() {
    return <div className={ActionButtons}>
      <ButtonWidget
        buttonType={ButtonType.Outline}
        testId={UploadBundlesDialogTestIDs.CANCEL}
        children={Messages.cancel()}
        handleClick={handleCancel}
      />
      <span className={ActionButtonLeftWide}>
        <ButtonWithSpinner
          disabled={!props.isValid}
          buttonType={ButtonType.Primary}
          showSpinner={isBusy}
          testId={UploadBundlesDialogTestIDs.SUBMIT}
          children={Messages.publish()}
          onClick={handleSubmit}
        />
      </span>
    </div>;
  }
}

interface PermissionsOptionProps {
  readonly handleChange: (permission: BundleContentPermissions) => void;
}

const getTooltip = (): JSX.Element => {
  const tooltipProps: TooltipProps = {
    getContent: () => <span style={{ fontSize: 12.5 }}>{Messages.connectToSalesforce_projectPermissionInfoTip()}</span>,
    position: TooltipPosition.BottomRightCorner,
    maxWidth: 200,
  };
  const tooltipPath = imgPath('info_normal.svg');

  return <Tooltip {...tooltipProps}>
  <img style={UploadBundleDialogStyles.PermissionsTooltip} src={tooltipPath} alt={''} />
  </Tooltip>;
};

function ProjectPermissionOptions(props: PermissionsOptionProps) {
  const [index, setIndex] = React.useState<number>(0);

  const buttonList = [
    {
      label: Messages.connectToSalesforce_projectPermissionPrivate(),
      testId: 'admin-only-option',
      value: BundleContentPermissions.adminOnly,
    },
    {
      label: Messages.connectToSalesforce_projectPermissionInherit(),
      testId: 'inherited-option',
      value: BundleContentPermissions.inherited,
    },
  ];
  return <>
    <div id={PERMISSION_OPTIONS_GROUP_LABEL_ID}>{Messages.connectToSalesforce_projectPermissions()}
      {getTooltip()}
    </div>
    <RadioButtonWithLabelGroupWidget
      aria-labelledby={PERMISSION_OPTIONS_GROUP_LABEL_ID}
      testId={UploadBundlesDialogTestIDs.PERMISSIONS}
      buttonList={buttonList}
      checkedIndex={index}
      handleChange={(value, index) => {
        props.handleChange(value);
        setIndex(index);
      }}
    />
  </>;
}

interface CloudOptionProps {
  readonly handleChange: (bundle: KnownBundlesKeys) => void;
  readonly buttonList: RadioButtonWithLabelOption<KnownBundlesKeys>[];
}

function CloudOptions(props: CloudOptionProps) {
  const [index, setIndex] = React.useState<number>(0);

  return <>
    <div id={CLOUD_OPTIONS_GROUP_LABEL_ID}>{Messages.connectToSalesforce_clouds()}</div>
    <RadioButtonWithLabelGroupWidget
      testId={UploadBundlesDialogTestIDs.CLOUD_OPTIONS}
      aria-labelledby={CLOUD_OPTIONS_GROUP_LABEL_ID}
      buttonList={props.buttonList}
      checkedIndex={index}
      handleChange={(value, index) => {
        props.handleChange(value);
        setIndex(index);
      }}
    />
  </>;
}

function getTopLevelProjectsWithName(projectName: string): Promise<number> {
  return Projects.all()
    .withFilters([Filter.fieldEq(FilterFieldNames.topLevelProject, true),
    Filter.fieldEq(FilterFieldNames.name, projectName)])
    .getCount();
}

interface UploadBundlesDialogErrors {
  projectName?: string;
}

function validateFormFields(values: UploadBundlesDialogValues): UploadBundlesDialogErrors {
  const errors: UploadBundlesDialogErrors = {};
  const isOnlySpaces = values.projectName.trim().length === 0 ? true : false;

  if (_.isEmpty(values.projectName) || isOnlySpaces) {
    errors.projectName = Messages.connectToSalesforce_projectNameBlankError();
  }

  return errors;
}

export const UploadBundlesDialog = withFormik<UploadBundlesDialogProps, UploadBundlesDialogValues>({
  mapPropsToValues: (props) => {
    return {
      bundleIds: [KnownBundles.nonprofitCloud],
      projectName: 'Salesforce',
      projectDescription: Messages.connectToSalesforce_projectDescription({
        SCHEDULE_REFRESHES_LINK_TEXT: Messages.connectToSalesforce_projectDescriptionScheduleRefreshesLinkText(),
        SCHEDULE_REFRESHES_HELP_LINK: HelpService.getHelpUrlByCategory('schedule-refreshes'),
        PERMISSIONS_LINK_TEXT: Messages.connectToSalesforce_projectDescriptionPermissionsLinkText(),
        PERMISSIONS_HELP_LINK: HelpService.getHelpUrlByCategory('server-permissions'),
      }),
      contentPermissions: BundleContentPermissions.adminOnly,
    };
  },
  validate: (values) => validateFormFields(values),
  validateOnMount: true,
  handleSubmit: (values, actions) => {}
})(InternalUploadBundlesDialogForm);
